import type {
  DailyPurchaseDefault,
  DailyPurchaseGroup,
  Dish,
  ExtraPurchaseItem,
  FixedPurchaseOccurrence,
  IngredientLine,
  Meal,
  PlanDay,
  PurchaseAggregation,
  PurchaseItem,
  PurchaseSource,
  RecipeSnapshot,
  WeeklyPlan,
} from '../types';
import { formatShortDate, weekdayLabel } from './date';
import {
  DEFAULT_DAILY_PURCHASE_DEFAULTS,
  groupPurchaseItemsByCategory,
  normalizeIngredientCategory,
} from './ingredient-categories';

const SCALE = 1000;

function dayHasService(day: PlanDay): boolean {
  return day.mealService
    ? day.mealService.breakfast || day.mealService.lunch
    : day.serviceEnabled;
}

export function dailyPurchaseDefaultAppliesToDate(
  item: Pick<DailyPurchaseDefault, 'weekdayMask'>,
  date: string,
): boolean {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay();
  if (!Number.isInteger(day)) return false;
  const mask = Number.isInteger(item.weekdayMask) ? Number(item.weekdayMask) : 127;
  return (mask & (1 << day)) !== 0;
}

export function ingredientKey(name: string, unit: string): string {
  return `${name.trim()}\u0000${unit.trim()}`;
}

export function dailyIngredientKey(date: string, name: string, unit: string): string {
  return `${date}\u0000${ingredientKey(name, unit)}`;
}

function toScaled(value: string | number): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.round(numeric * SCALE);
}

function fromScaled(value: number): string {
  const normalized = (value / SCALE).toFixed(3).replace(/\.0+$|(?<=\.[0-9]*?)0+$/g, '');
  return normalized.endsWith('.') ? normalized.slice(0, -1) : normalized;
}

export function cleanQuantity(value: string | number): string {
  return fromScaled(toScaled(value));
}

function extraItemAsSelection(item: ExtraPurchaseItem): {
  dishId: string;
  batchCount: number;
  recipeSnapshot: RecipeSnapshot;
} {
  const sourceLabel = item.sourceType === 'ingredient' ? '直接采购食材' : '自定义采购';
  return {
    dishId: `extra_item_${item.id ?? `${item.name}-${item.unit}`}`,
    batchCount: 1,
    recipeSnapshot: {
      dishName: `${sourceLabel}：${item.name}`,
      ingredients: [{
        name: item.name,
        quantity: item.quantity,
        unit: item.unit,
        ingredientCategory: item.ingredientCategory,
      }],
    },
  };
}

export function aggregatePurchase(plan: WeeklyPlan, dishes: Dish[]): PurchaseItem[] {
  const dishesById = new Map(dishes.map((dish) => [dish.id, dish]));
  const totals = new Map<string, MutablePurchaseTotal>();
  let position = 0;

  const addRecipe = ({
    dishId,
    batchCount,
    recipeSnapshot,
    kind,
    date,
    meal,
  }: {
    dishId: string;
    batchCount: number;
    recipeSnapshot?: RecipeSnapshot;
    kind: 'meal' | 'extra';
    date?: string;
    meal?: Meal;
  }) => {
    const liveDish = dishesById.get(dishId);
    const recipe = recipeSnapshot?.ingredients ?? liveDish?.ingredients ?? [];
    const dishName = recipeSnapshot?.dishName ?? liveDish?.name ?? '已停用菜品';
    for (const ingredient of recipe) {
      const key = ingredientKey(ingredient.name, ingredient.unit);
      const contributionScaled = toScaled(ingredient.quantity) * batchCount;
      const current = totals.get(key) ?? {
        name: ingredient.name.trim(),
        unit: ingredient.unit.trim(),
        ingredientCategory: normalizeIngredientCategory(ingredient.ingredientCategory, ingredient.name),
        scaled: 0,
        firstSeen: position++,
        sources: [],
      };
      current.scaled += contributionScaled;
      current.sources.push({
        kind,
        ...(date ? { date } : {}),
        ...(meal ? { meal } : {}),
        dishId,
        dishName,
        batchCount,
        contributionQuantity: fromScaled(contributionScaled),
        unit: ingredient.unit.trim(),
      });
      totals.set(key, current);
    }
  };

  for (const day of plan.days) {
    if (!dayHasService(day)) continue;
    for (const selection of day.selections) {
      const mealEnabled = day.mealService?.[selection.meal] ?? day.serviceEnabled;
      if (!mealEnabled) continue;
      addRecipe({ ...selection, kind: 'meal', date: day.date });
    }
  }

  for (const selection of plan.extraSelections ?? []) {
    addRecipe({ ...selection, kind: 'extra' });
  }
  for (const item of plan.extraItems ?? []) {
    addRecipe({ ...extraItemAsSelection(item), kind: 'extra' });
  }

  return [...totals.entries()]
    .sort(([, a], [, b]) => a.firstSeen - b.firstSeen)
    .map(([key, item]) => {
      const calculatedQuantity = fromScaled(item.scaled);
      return {
        key,
        name: item.name,
        unit: item.unit,
        calculatedQuantity,
        finalQuantity: cleanQuantity(plan.purchaseOverrides[key] ?? calculatedQuantity),
        ingredientCategory: item.ingredientCategory,
        sources: item.sources,
      };
    });
}

type MutablePurchaseTotal = {
  name: string;
  unit: string;
  ingredientCategory: PurchaseItem['ingredientCategory'];
  scaled: number;
  firstSeen: number;
  sources: PurchaseSource[];
};

function addRecipeToTotals(
  totals: Map<string, MutablePurchaseTotal>,
  dishesById: Map<string, Dish>,
  selection: {
    dishId: string;
    batchCount: number;
    recipeSnapshot?: RecipeSnapshot;
  },
  source: Pick<PurchaseSource, 'kind' | 'date' | 'meal'>,
  nextPosition: () => number,
): void {
  const liveDish = dishesById.get(selection.dishId);
  const recipe = selection.recipeSnapshot?.ingredients ?? liveDish?.ingredients ?? [];
  const dishName = selection.recipeSnapshot?.dishName ?? liveDish?.name ?? '已停用菜品';

  for (const ingredient of recipe) {
    const key = ingredientKey(ingredient.name, ingredient.unit);
    const contributionScaled = toScaled(ingredient.quantity) * selection.batchCount;
    const current = totals.get(key) ?? {
      name: ingredient.name.trim(),
      unit: ingredient.unit.trim(),
      ingredientCategory: normalizeIngredientCategory(ingredient.ingredientCategory, ingredient.name),
      scaled: 0,
      firstSeen: nextPosition(),
      sources: [],
    };
    current.scaled += contributionScaled;
    current.sources.push({
      ...source,
      dishId: selection.dishId,
      dishName,
      batchCount: selection.batchCount,
      contributionQuantity: fromScaled(contributionScaled),
      unit: ingredient.unit.trim(),
    });
    totals.set(key, current);
  }
}

function addDailyDefaultToTotals(
  totals: Map<string, MutablePurchaseTotal>,
  item: DailyPurchaseDefault,
  date: string,
  nextPosition: () => number,
): void {
  const name = item.name.trim();
  const unit = item.unit.trim();
  const key = ingredientKey(name, unit);
  const contributionScaled = toScaled(item.quantity);
  const current = totals.get(key) ?? {
    name,
    unit,
    ingredientCategory: normalizeIngredientCategory(item.ingredientCategory, name),
    scaled: 0,
    firstSeen: nextPosition(),
    sources: [],
  };
  current.scaled += contributionScaled;
  current.sources.push({
    kind: 'daily_default',
    date,
    dishId: item.id ?? `daily-default-${name}-${unit}`,
    dishName: '每日固定备料',
    batchCount: 1,
    contributionQuantity: fromScaled(contributionScaled),
    unit,
  });
  totals.set(key, current);
}

function addFixedOccurrenceToTotals(
  totals: Map<string, MutablePurchaseTotal>,
  occurrence: FixedPurchaseOccurrence,
  nextPosition: () => number,
): void {
  if (occurrence.quantity !== undefined) {
    const name = occurrence.name.trim();
    const snapshotUnit = occurrence.sourceSnapshot.sourceType === 'dish'
      ? occurrence.sourceSnapshot.ingredients[0]?.unit
      : occurrence.sourceSnapshot.unit;
    const unit = (occurrence.unit ?? snapshotUnit ?? '').trim();
    if (!name || !unit) return;
    const key = ingredientKey(name, unit);
    const contributionScaled = toScaled(occurrence.quantity);
    const current: MutablePurchaseTotal = totals.get(key) ?? {
      name,
      unit,
      ingredientCategory: normalizeIngredientCategory(occurrence.ingredientCategory, name),
      scaled: 0,
      firstSeen: nextPosition(),
      sources: [],
    };
    current.scaled += contributionScaled;
    current.sources.push({
      kind: occurrence.date ? 'daily_default' : 'extra',
      ...(occurrence.date ? { date: occurrence.date } : {}),
      dishId: occurrence.ruleId,
      dishName: `固定采购：${occurrence.name}`,
      batchCount: 1,
      contributionQuantity: fromScaled(contributionScaled),
      unit,
    });
    totals.set(key, current);
    return;
  }

  if (occurrence.sourceType === 'dish' && occurrence.sourceSnapshot.sourceType === 'dish') {
    const batchCount = occurrence.batchCount ?? 1;
    for (const ingredient of occurrence.sourceSnapshot.ingredients) {
      const name = ingredient.name.trim();
      const unit = ingredient.unit.trim();
      const key = ingredientKey(name, unit);
      const contributionScaled = toScaled(ingredient.quantity) * batchCount;
      const current: MutablePurchaseTotal = totals.get(key) ?? {
        name,
        unit,
        ingredientCategory: normalizeIngredientCategory(ingredient.ingredientCategory, name),
        scaled: 0,
        firstSeen: nextPosition(),
        sources: [],
      };
      current.scaled += contributionScaled;
      current.sources.push({
        kind: occurrence.date ? 'daily_default' : 'extra',
        ...(occurrence.date ? { date: occurrence.date } : {}),
        dishId: occurrence.ruleId,
        dishName: `固定采购：${occurrence.name}`,
        batchCount,
        contributionQuantity: fromScaled(contributionScaled),
        unit,
      });
      totals.set(key, current);
    }
    return;
  }

}

function isLegacyFixedSelection(
  selection: { dishId: string; recipeSnapshot?: RecipeSnapshot },
  weeklyOccurrences: FixedPurchaseOccurrence[],
): boolean {
  const dishName = selection.recipeSnapshot?.dishName?.trim();
  return weeklyOccurrences.some((occurrence) => {
    if (occurrence.sourceType === 'dish' && occurrence.sourceId === selection.dishId) return true;
    if (!dishName || dishName !== occurrence.name.trim()) return false;
    const ingredients = selection.recipeSnapshot?.ingredients ?? [];
    return ingredients.length === 1
      && ingredients[0].name.trim() === occurrence.name.trim()
      && ingredients[0].unit.trim() === (occurrence.unit ?? '').trim();
  });
}

function totalsToItems(
  totals: Map<string, MutablePurchaseTotal>,
  keyFor: (name: string, unit: string) => string,
  overrides: Record<string, string>,
): PurchaseItem[] {
  return [...totals.values()]
    .sort((a, b) => a.firstSeen - b.firstSeen)
    .map((item) => {
      const key = keyFor(item.name, item.unit);
      const calculatedQuantity = fromScaled(item.scaled);
      return {
        key,
        name: item.name,
        unit: item.unit,
        calculatedQuantity,
        finalQuantity: cleanQuantity(overrides[key] ?? calculatedQuantity),
        ingredientCategory: item.ingredientCategory,
        sources: item.sources,
      };
    });
}

/**
 * Builds the current purchase view: breakfast and lunch are merged within a
 * service date, while different dates are intentionally kept separate.
 */
export function aggregatePurchaseByDay(
  plan: WeeklyPlan,
  dishes: Dish[],
  dailyPurchaseDefaults: DailyPurchaseDefault[] = DEFAULT_DAILY_PURCHASE_DEFAULTS,
): PurchaseAggregation {
  const dishesById = new Map(dishes.map((dish) => [dish.id, dish]));
  const dailyPurchaseGroups: DailyPurchaseGroup[] = [];
  const dailyOverrides = plan.dailyPurchaseOverrides ?? {};
  const resolvedFixed = (plan.fixedPurchaseRulesRevision ?? 0) > 0
    ? plan.resolvedFixedPurchases
    : undefined;

  for (const day of plan.days) {
    if (!dayHasService(day)) continue;
    const totals = new Map<string, MutablePurchaseTotal>();
    let position = 0;
    const nextPosition = () => position++;

    if (resolvedFixed) {
      for (const occurrence of resolvedFixed.daily.filter((item) => item.date === day.date)) {
        addFixedOccurrenceToTotals(totals, occurrence, nextPosition);
      }
    } else {
      for (const item of dailyPurchaseDefaults.filter((defaultItem) => (
        dailyPurchaseDefaultAppliesToDate(defaultItem, day.date)
      ))) {
        addDailyDefaultToTotals(totals, item, day.date, nextPosition);
      }
    }

    for (const selection of day.selections) {
      const mealEnabled = day.mealService?.[selection.meal] ?? day.serviceEnabled;
      if (!mealEnabled) continue;
      addRecipeToTotals(
        totals,
        dishesById,
        selection,
        { kind: 'meal', date: day.date, meal: selection.meal },
        nextPosition,
      );
    }

    const items = totalsToItems(
      totals,
      (name, unit) => dailyIngredientKey(day.date, name, unit),
      dailyOverrides,
    );
    if (items.length) dailyPurchaseGroups.push({ date: day.date, items });
  }

  const extraTotals = new Map<string, MutablePurchaseTotal>();
  let extraPosition = 0;
  const weeklyFixed = resolvedFixed?.weekly ?? [];
  for (const selection of plan.extraSelections ?? []) {
    if (resolvedFixed && isLegacyFixedSelection(selection, weeklyFixed)) continue;
    addRecipeToTotals(
      extraTotals,
      dishesById,
      selection,
      { kind: 'extra' },
      () => extraPosition++,
    );
  }
  for (const item of plan.extraItems ?? []) {
    addRecipeToTotals(
      extraTotals,
      dishesById,
      extraItemAsSelection(item),
      { kind: 'extra' },
      () => extraPosition++,
    );
  }
  for (const occurrence of weeklyFixed) {
    addFixedOccurrenceToTotals(extraTotals, occurrence, () => extraPosition++);
  }
  const extraPurchaseItems = totalsToItems(extraTotals, ingredientKey, plan.purchaseOverrides ?? {});

  return {
    dailyPurchaseGroups,
    extraPurchaseItems,
    purchaseItems: [
      ...dailyPurchaseGroups.flatMap((group) => group.items),
      ...extraPurchaseItems,
    ],
  };
}

function retainOverrideKeys(
  overrides: Record<string, string> | undefined,
  validKeys: Set<string>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(overrides ?? {}).filter(([key]) => validKeys.has(key)),
  );
}

function equalOverrideMaps(
  left: Record<string, string> | undefined,
  right: Record<string, string>,
): boolean {
  const entries = Object.entries(left ?? {});
  return entries.length === Object.keys(right).length
    && entries.every(([key, value]) => right[key] === value);
}

/** Removes invisible quantity edits whose meal, dish or extra purchase no longer exists. */
export function prunePurchaseOverrides(
  plan: WeeklyPlan,
  dishes: Dish[],
  dailyPurchaseDefaults: DailyPurchaseDefault[] = DEFAULT_DAILY_PURCHASE_DEFAULTS,
): WeeklyPlan {
  const aggregation = aggregatePurchaseByDay(plan, dishes, dailyPurchaseDefaults);
  const dailyKeys = new Set(
    aggregation.dailyPurchaseGroups.flatMap((group) => group.items.map((item) => item.key)),
  );
  const extraKeys = new Set(aggregation.extraPurchaseItems.map((item) => item.key));
  const dailyPurchaseOverrides = retainOverrideKeys(plan.dailyPurchaseOverrides, dailyKeys);
  const purchaseOverrides = retainOverrideKeys(plan.purchaseOverrides, extraKeys);

  if (
    equalOverrideMaps(plan.dailyPurchaseOverrides, dailyPurchaseOverrides)
    && equalOverrideMaps(plan.purchaseOverrides, purchaseOverrides)
  ) {
    return plan;
  }

  return {
    ...plan,
    dailyPurchaseOverrides,
    purchaseOverrides,
  };
}

export function ingredientUnitWarnings(items: PurchaseItem[]): string[] {
  const units = new Map<string, Set<string>>();
  for (const item of items) {
    const seen = units.get(item.name) ?? new Set<string>();
    seen.add(item.unit);
    units.set(item.name, seen);
  }
  return [...units.entries()]
    .filter(([, value]) => value.size > 1)
    .map(([name, value]) => `${name} 同时使用了 ${[...value].join('、')}，已分开统计`);
}

export function buildWechatText(
  kitchenName: string,
  weekLabel: string,
  items: Pick<PurchaseItem, 'name' | 'unit' | 'finalQuantity'>[],
): string {
  const lines = items.map((item, index) => `${index + 1}. ${item.name}：${item.finalQuantity}${item.unit}`);
  return `【${kitchenName}备菜采购清单】\n时间：${weekLabel}\n\n${lines.join('\n')}`;
}

export function buildDailyWechatText(
  kitchenName: string,
  weekLabel: string,
  dailyPurchaseGroups: DailyPurchaseGroup[],
  extraPurchaseItems: Pick<PurchaseItem, 'name' | 'unit' | 'finalQuantity'>[],
): string {
  const sections = dailyPurchaseGroups.map((group) => {
    const categories = groupPurchaseItemsByCategory(group.items).map(({ category, items }) => {
      const lines = items.map((item, index) => (
        `${index + 1}. ${item.name}：${item.finalQuantity}${item.unit}`
      ));
      return `【${category}】\n${lines.join('\n')}`;
    });
    return `【${formatShortDate(group.date)} ${weekdayLabel(group.date)}】\n${categories.join('\n')}`;
  });

  if (extraPurchaseItems.length) {
    const categories = groupPurchaseItemsByCategory(extraPurchaseItems).map(({ category, items }) => {
      const lines = items.map((item, index) => (
        `${index + 1}. ${item.name}：${item.finalQuantity}${item.unit}`
      ));
      return `【${category}】\n${lines.join('\n')}`;
    });
    sections.push(`【本周额外采购】\n${categories.join('\n')}`);
  }

  return `【${kitchenName}备菜采购清单】\n时间：${weekLabel}\n\n${sections.join('\n\n')}`;
}

export function snapshotIngredients(dish: Dish): IngredientLine[] {
  return dish.ingredients.map((ingredient) => ({ ...ingredient }));
}
